import { WorkOrderData } from '@/api/workOrders/types';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extended once, at module load: every `dayjs.tz` call in the app relies on it.
dayjs.extend(utc);
dayjs.extend(timezone);

// Company timezone (calendar blockages) and fallback for appointments that
// arrive without one. See docs/guideline/datetime.md.
export const defaultTimezone = 'America/Sao_Paulo';

// Mirrors the backend rule that derives an appointment's timezone from the
// property's state; any other UF shares São Paulo's clock.
const ufTimezones: Record<string, string> = {
  AC: 'America/Rio_Branco',
  AM: 'America/Manaus',
  MS: 'America/Campo_Grande',
  MT: 'America/Cuiaba',
  RO: 'America/Porto_Velho',
  RR: 'America/Boa_Vista',
};

export const timezoneOfUf = (uf: string | null | undefined): string =>
  (uf && ufTimezones[uf.toUpperCase()]) || defaultTimezone;

export const appointmentTimezone = (
  workOrder: Pick<WorkOrderData, 'inspection' | 'state'> | undefined
): string => workOrder?.inspection?.timezone ?? timezoneOfUf(workOrder?.state);

const wallClockFormat = 'YYYY-MM-DDTHH:mm:ss';

/**
 * The instant as the wall clock of `tz`, rebuilt as a browser-local Dayjs.
 * Pickers and FullCalendar only speak the browser's zone, so they get the
 * property's hours as if they were local.
 */
export const toWallClock = (instant: string, tz?: string | null): Dayjs =>
  dayjs(
    dayjs(instant)
      .tz(tz || defaultTimezone)
      .format(wallClockFormat)
  );

/** Inverse of `toWallClock`: local fields read in `tz`, sent as a UTC ISO. */
export const fromWallClock = (local: Dayjs, tz: string): string =>
  dayjs.tz(local.format(wallClockFormat), tz).toISOString();

/** `toWallClock` as the naive string FullCalendar draws without converting. */
export const toCalendarDate = (instant: string, tz?: string | null): string =>
  toWallClock(instant, tz).format(wallClockFormat);
